/**
 * Market Module — Shared Types & Constants
 * 
 * Core data structures for collection management, multi-vendor pricing,
 * smart cart, and local game store integration.
 */

import { Card } from '../../App';

// ============================================================================
// COLLECTION TYPES
// ============================================================================

/** A single card in a user's collection */
export interface CollectionCard {
    id: string;                  // Unique collection entry ID
    cardName: string;            // Card name (matches Card.name)
    scryfallId?: string;         // Scryfall card ID for exact printing
    quantity: number;            // How many copies owned
    condition: CardCondition;    // Physical condition
    isFoil: boolean;             // Foil vs non-foil
    setCode?: string;            // Set code (e.g., "dmu")
    setName?: string;            // Set name (e.g., "Dominaria United")
    language?: string;           // Language code (default: "en")
    imageUrl?: string;           // Card image URL
    priceUsd?: number | null;    // Last known USD price
    priceEur?: number | null;    // Last known EUR price
    notes?: string;              // User notes (e.g., "signed copy")
    forTrade: boolean;           // Is this card available for trade?
    forSale: boolean;            // Is this card listed for sale?
    askingPrice?: number | null; // Manual asking price (if for sale)
    addedAt: string;             // ISO timestamp when added
    updatedAt: string;           // ISO timestamp when last updated
    // Card data (populated from Scryfall/cache)
    type?: string;
    manaCost?: string;
    cmc?: number;
    colors?: string[];
    rarity?: string;
    oracleText?: string;
}

/** Card condition grades */
export type CardCondition = 'NM' | 'LP' | 'MP' | 'HP' | 'DMG';

/** Condition multipliers for price estimation */
export const CONDITION_MULTIPLIERS: Record<CardCondition, { label: string; multiplier: number }> = {
    NM: { label: 'Near Mint', multiplier: 1.0 },
    LP: { label: 'Lightly Played', multiplier: 0.9 },
    MP: { label: 'Moderately Played', multiplier: 0.75 },
    HP: { label: 'Heavily Played', multiplier: 0.6 },
    DMG: { label: 'Damaged', multiplier: 0.4 },
};

// ============================================================================
// MULTI-VENDOR PRICING
// ============================================================================

/** Price data from multiple vendors for a single card */
export interface VendorPricing {
    cardName: string;
    tcgplayer: VendorPrice | null;
    cardKingdom: VendorPrice | null;
    cardmarket: VendorPrice | null;
    cheapest: 'tcgplayer' | 'cardKingdom' | 'cardmarket' | null;
    lastUpdated: string;
}

export interface VendorPrice {
    price: number;
    foilPrice?: number;
    url: string;
    stock: 'in_stock' | 'low_stock' | 'out_of_stock' | 'unknown';
    vendor: string;
}

/** Vendor branding */
export const VENDORS = {
    tcgplayer: {
        name: 'TCGPlayer',
        color: '#F48F2A',
        bgColor: 'rgba(244,143,42,0.15)',
        borderColor: 'rgba(244,143,42,0.3)',
        icon: '🛒',
    },
    cardKingdom: {
        name: 'Card Kingdom',
        color: '#2563EB',
        bgColor: 'rgba(37,99,235,0.15)',
        borderColor: 'rgba(37,99,235,0.3)',
        icon: '👑',
    },
    cardmarket: {
        name: 'Cardmarket',
        color: '#0EA5E9',
        bgColor: 'rgba(14,165,233,0.15)',
        borderColor: 'rgba(14,165,233,0.3)',
        icon: '🌍',
    },
} as const;

export type VendorKey = keyof typeof VENDORS;

// ============================================================================
// SMART CART
// ============================================================================

/** A cart item with optimized vendor selection */
export interface CartItem {
    cardName: string;
    quantity: number;
    condition: CardCondition;
    isFoil: boolean;
    selectedVendor: VendorKey;
    unitPrice: number;
    totalPrice: number;
    imageUrl?: string;
    setCode?: string;
    type?: string;
    rarity?: string;
    cmc?: number;
    allPrices: VendorPricing;
}

/** Cart summary by vendor */
export interface CartSummary {
    totalItems: number;
    totalCards: number;
    totalPrice: number;
    vendorBreakdown: Record<VendorKey, { items: number; cards: number; total: number }>;
    savings: number; // How much saved vs buying all from most expensive
}

// ============================================================================
// STORE LOCATOR
// ============================================================================

/** A local game store */
export interface GameStore {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    phone?: string;
    website?: string;
    email?: string;
    distance?: number;          // Miles from user
    lat: number;
    lng: number;
    isWpnStore: boolean;        // Wizards Play Network store
    isPremium?: boolean;        // WPN Premium store
    storeType: 'lgs' | 'online' | 'big_box';
    hasEvents: boolean;
    rating?: number;            // 1-5
}

/** Geolocation coordinates */
export interface GeoPosition {
    lat: number;
    lng: number;
    accuracy?: number;
}

// ============================================================================
// ISO LIST (In Search Of)
// ============================================================================

/** An item on a user's ISO (want) list */
export interface ISOListItem {
    id: string;
    userId?: string;
    cardName: string;
    scryfallId?: string;
    setCode?: string;
    setName?: string;
    maxPrice?: number | null;
    minCondition: CardCondition;
    foilPreference: 'foil' | 'nonfoil' | 'either';
    quantity: number;
    priority: 'high' | 'medium' | 'low';
    notes?: string;
    imageUrl?: string;
    isActive: boolean;
    matchedCount: number;
    lastMatchedAt?: string;
    createdAt: string;
    updatedAt: string;
}

/** A card listing from a seller/trader */
export interface CardListing {
    id: string;
    userId: string;
    cardName: string;
    scryfallId?: string;
    setCode?: string;
    setName?: string;
    listingType: 'trade' | 'sale' | 'both';
    condition: CardCondition;
    isFoil: boolean;
    quantity: number;
    askingPrice?: number | null;
    currency: 'usd' | 'eur';
    latitude?: number;
    longitude?: number;
    locationName?: string;
    imageUrl?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

/** A match notification */
export interface ISONotification {
    id: string;
    userId: string;
    isoItemId: string;
    listingId: string;
    listerId: string;
    isRead: boolean;
    isDismissed: boolean;
    createdAt: string;
    // Populated via join
    isoItem?: ISOListItem;
    listing?: CardListing;
}

/** Priority colors */
export const ISO_PRIORITY_COLORS = {
    high: { text: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', label: '🔥 High' },
    medium: { text: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.25)', label: 'Medium' },
    low: { text: '#6b7280', bg: 'rgba(107,114,128,0.10)', border: 'rgba(107,114,128,0.2)', label: 'Low' },
} as const;

// ============================================================================
// MARKET SUB-VIEW TYPES
// ============================================================================

/** Views within the MARKET tab */
export type MarketView = 'pricing' | 'collection' | 'cart' | 'iso' | 'stores';

/** Rarity colors (shared) */
export const RARITY_COLORS: Record<string, { text: string; bg: string; border: string }> = {
    M: { text: '#e6a124', bg: 'rgba(230,161,36,0.15)', border: 'rgba(230,161,36,0.3)' },
    R: { text: '#c9a34a', bg: 'rgba(201,163,74,0.12)', border: 'rgba(201,163,74,0.25)' },
    U: { text: '#8cb4d0', bg: 'rgba(140,180,208,0.12)', border: 'rgba(140,180,208,0.25)' },
    C: { text: '#808080', bg: 'rgba(128,128,128,0.10)', border: 'rgba(128,128,128,0.2)' },
    B: { text: '#808080', bg: 'rgba(128,128,128,0.10)', border: 'rgba(128,128,128,0.2)' },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/** Format price with currency symbol */
export function formatPrice(price: number | null | undefined, currency: 'usd' | 'eur' = 'usd'): string {
    if (price == null || isNaN(price)) return '—';
    const symbol = currency === 'eur' ? '€' : '$';
    return `${symbol}${price.toFixed(2)}`;
}

/** Build TCGPlayer mass entry cart URL */
export function buildTCGPlayerCartUrl(cards: Array<{ name: string; quantity: number; priceUsd?: number | null }>): string {
    const entries = cards
        .filter(c => (c.priceUsd ?? null) != null)
        .map(c => {
            const name = c.name.includes(' // ') ? c.name.split(' // ')[0] : c.name;
            return `${c.quantity} ${name}`;
        })
        .join('||');
    return `https://www.tcgplayer.com/massentry?partner=DECKSTRGG&utm_campaign=affiliate&utm_medium=DECKSTRGG&utm_source=DECKSTRGG&c=${encodeURIComponent(entries)}`;
}

/** Build Card Kingdom search URL */
export function buildCardKingdomUrl(name: string): string {
    const cleanName = name.includes(' // ') ? name.split(' // ')[0] : name;
    return `https://www.cardkingdom.com/catalog/search?search=header&filter%5Bname%5D=${encodeURIComponent(cleanName)}&partner=DECKSTRGG&utm_source=DECKSTRGG&utm_medium=affiliate&utm_campaign=DECKSTRGG`;
}

/** Build Cardmarket search URL */
export function buildCardmarketUrl(name: string): string {
    const cleanName = name.includes(' // ') ? name.split(' // ')[0] : name;
    return `https://www.cardmarket.com/en/Magic/Products/Search?searchString=${encodeURIComponent(cleanName)}&utm_source=DECKSTRGG`;
}

/** Generate a unique ID */
export function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/** Rate-limited fetch for Scryfall API */
export async function rateLimitedFetch(url: string, delayMs: number = 100): Promise<Response> {
    await new Promise(resolve => setTimeout(resolve, delayMs));
    return fetch(url);
}
